import type { BusinessBriefData } from '@doneverse/shared'
import { isFieldVisible } from '@doneverse/shared'
import { z } from 'zod'
import type { SchemaField } from './schema-field.js'

export type BriefValues = Record<string, string | string[]>

/** Convert BusinessBriefData (JSON with mixed types) to BriefValues (string/string[]). */
export function toBriefValues(brief: BusinessBriefData | null | undefined): BriefValues {
  const values: BriefValues = {}

  if (!brief) return values

  for (const [key, value] of Object.entries(brief)) {
    if (Array.isArray(value)) {
      values[key] = value.map(String)
    } else if (value != null) {
      values[key] = String(value)
    }
  }

  return values
}

/** Build a Zod schema from dynamic SchemaField[] with required field validation. */
export function buildBriefFormSchema(fields: SchemaField[]) {
  const shape: Record<string, z.ZodTypeAny> = {}

  const coerceToStringArray = z.preprocess((val) => {
    if (val == null) return []
    if (typeof val === 'string') {
      return val
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    }
    return val
  }, z.array(z.string()))

  for (const field of fields) {
    if (field.type === 'section') continue
    const fieldId = String(field.id)
    if (field.field_type === 'rank') {
      shape[fieldId] = coerceToStringArray.default(field.options ?? [])
    } else if (field.field_type === 'multi_select' || field.displayAs === 'multi_list') {
      shape[fieldId] = coerceToStringArray.default([])
    } else {
      shape[fieldId] = z.string().default('')
    }
  }

  return z.object(shape).superRefine((data, ctx) => {
    for (const field of fields) {
      if (field.type === 'section') continue
      if (!field.required) continue

      // Skip hidden conditional fields.
      if (!isFieldVisible(field.showWhen, data)) continue

      const fieldId = String(field.id)
      const value = data[fieldId]
      const isEmpty = !value || (Array.isArray(value) ? value.length === 0 : !value.trim())

      if (isEmpty) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'This field is required',
          path: [fieldId],
        })
      }
    }
  })
}
